import { StripeInvoiceProvider } from "@/providers/StripeInvoiceProvider";
import { SupportTicketsProvider } from "@/providers/SupportTicketsProvider";
import { ServiceOrdersProvider } from "@/providers/ServiceOrdersProvider";
import { ChangeRequestsProvider } from "@/providers/ChangeRequestsProvider";
import { ClientDataProvider, useClientData } from "./providers/ClientDataProvider";
import { BillingProvider } from "@/providers/BillingProvider";
import { WorkOrdersProvider } from "@/providers/WorkOrdersProvider";
import { Route, Routes } from "react-router-dom";
import { FeedbackProvider } from "@/providers/FeedbackProvider";
import { PortalLayout } from "@/components/portal/PortalLayout";
import {
  OverviewPage,
  ServiceOrdersPage,
  WorkOrderDetailPage,
  AgreementsPage,
  BillingPage,
  DisputesPage,
  PortalServicesPage,
  MeetingsPage,
  CreateSolutionPage,
} from "@/pages/portal";
import { InvoiceDetailPage } from "@/pages/portal/InvoiceDetailPage";
import { SupportPage } from "@/pages/portal/SupportPage";
import { TicketDetailPage as SupportTicketDetailPage } from "@/pages/portal/SupportPage/TicketDetailPage";
import FeedbackPage from "@/pages/portal/FeedbackPage";
import { IntegrationsProvider } from "@/providers/IntegrationsProvider";
import { useAuthContext } from "@/providers/AuthProvider";
import { ShieldX, LogOut } from "lucide-react";

function AccessDeniedScreen() {
  const { accessDeniedReason } = useClientData();
  const { logout } = useAuthContext();

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
          <ShieldX className="w-8 h-8 text-destructive" />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">
            Access Denied
          </h1>
          <p className="text-muted-foreground">
            {accessDeniedReason ||
              "Your account is not associated with an active client."}
          </p>
        </div>
        <p className="text-sm text-muted-foreground">
          If you believe this is an error, please contact us at{" "}
          <a
            href="mailto:support@echopeaklabs.com"
            className="text-primary underline underline-offset-4 hover:text-primary/80"
          >
            support@echopeaklabs.com
          </a>
        </p>
        <button
          onClick={logout}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90 transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </button>
      </div>
    </div>
  );
}

function ErrorScreen() {
  const { error, getInfo } = useClientData();
  const { logout } = useAuthContext();

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
          <ShieldX className="w-8 h-8 text-destructive" />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">
            Something went wrong
          </h1>
          <p className="text-muted-foreground">
            {error || "We couldn't verify your account. Please try again."}
          </p>
        </div>
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={getInfo}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90 transition-colors"
          >
            Retry
          </button>
          <button
            onClick={logout}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-input bg-background px-6 py-2.5 text-sm font-medium shadow-sm hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}

function PortalGate() {
  const { accessDenied, isFetchingClientInfo, error } = useClientData();

  if (isFetchingClientInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return <AccessDeniedScreen />;
  }

  if (error) {
    return <ErrorScreen />;
  }

  return (
    <StripeInvoiceProvider>
      <BillingProvider>
        <SupportTicketsProvider>
          <ServiceOrdersProvider>
            <ChangeRequestsProvider>
              <WorkOrdersProvider>
                <IntegrationsProvider>
                  <FeedbackProvider>
                    <Routes>
                      <Route path="/" element={<PortalLayout />}>
                        <Route index element={<OverviewPage />} />
                        <Route
                          path="service-orders"
                          element={<ServiceOrdersPage />}
                        />
                        <Route
                          path="service-orders/:workOrderId/*"
                          element={<WorkOrderDetailPage />}
                        />
                        <Route
                          path="create-solution"
                          element={<CreateSolutionPage />}
                        />
                        <Route
                          path="agreements"
                          element={<AgreementsPage />}
                        />
                        <Route path="invoices" element={<BillingPage />} />
                        <Route
                          path="invoices/:invoiceId/overview"
                          element={<InvoiceDetailPage />}
                        />
                        <Route
                          path="services"
                          element={<PortalServicesPage />}
                        />
                        <Route
                          path="meetings"
                          element={<MeetingsPage />}
                        />
                        <Route path="support" element={<SupportPage />} />
                        <Route
                          path="support/:ticketId"
                          element={<SupportTicketDetailPage />}
                        />
                        <Route path="feedback" element={<FeedbackPage />} />
                        <Route path="disputes" element={<DisputesPage />} />
                      </Route>
                    </Routes>
                  </FeedbackProvider>
                </IntegrationsProvider>
              </WorkOrdersProvider>
            </ChangeRequestsProvider>
          </ServiceOrdersProvider>
        </SupportTicketsProvider>
      </BillingProvider>
    </StripeInvoiceProvider>
  );
}

export const AuthenticatedApp = () => {
  return (
    <ClientDataProvider>
      <PortalGate />
    </ClientDataProvider>
  );
};
